import React, { useState } from 'react'

import { Box, Text, theme } from '@factorialco/deprecated-design-system'
import { F0Button, UpsellRequestResponseDialog } from '@factorialco/f0-react'
import { Cross } from '@factorialco/f0-react/icons/app'
import { InAppCommunicationQuery, Maybe } from 'generated/resources/graphql'

import { useEffect } from 'modules/core/hooks/useEffect'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import StepAnnouncementModal from 'modules/in_app_comms/components/AnnouncementModal/StepAnnouncementModal'
import CommsButton from 'modules/in_app_comms/components/CommsButton'
import Media from 'modules/in_app_comms/components/Media'

type ModalContent =
  InAppCommunicationQuery['inAppComms']['communicationsConnection']['nodes'][number]['content']['modal']

interface AnnouncementModalProps {
  content: Maybe<ModalContent> | undefined
  componentName: string
  onDismiss: () => void
}

const AnnouncementModal = ({ content, componentName, onDismiss }: AnnouncementModalProps) => {
  const [upsellSuccess, setUpsellSuccess] = useState(false)

  const handleClose = (e?: React.MouseEvent) => {
    e?.stopPropagation()
    onDismiss()
    track(trackingEvents.inAppComms.dismissInAppCommsComponent, {
      component: 'in_app_comms_modal',
      componentName,
    })
  }

  const handleUpsellSuccess = () => {
    setUpsellSuccess(true)
  }

  const handleUpsellError = () => {
    setUpsellSuccess(false)
  }

  const handleUpsellClose = () => {
    setUpsellSuccess(false)
    onDismiss()
  }

  useEffect(() => {
    if (!content?.title) return
    track(trackingEvents.inAppComms.viewInAppCommsComponent, {
      component: 'in_app_comms_modal',
      componentName,
    })
  }, [content?.title, componentName])

  if (!content) return null

  return (
    <>
      <Box
        width="full"
        height="full"
        justifyContent="center"
        alignItems="center"
        style={{
          background: 'rgba(0,0,0,0.3)',
          zIndex: theme.zIndex.modals,
          position: 'fixed',
        }}
      >
        {content.steps && content.steps.length > 0 ? (
          <StepAnnouncementModal
            content={content}
            componentName={componentName}
            onDismiss={onDismiss}
            handleClose={handleClose}
            handleUpsellSuccess={handleUpsellSuccess}
            handleUpsellError={handleUpsellError}
            handleUpsellClose={handleUpsellClose}
            upsellSuccess={upsellSuccess}
            upsellError={false}
          />
        ) : (
          <>
            <Box
              background="grey000White"
              borderRadius={{ all: 'abs016' }}
              width="s600"
              position="relative"
              style={{
                overflow: 'auto',
                maxHeight: '90vh',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <Box
                position="absolute"
                style={{
                  top: '8px',
                  right: '8px',
                  zIndex: theme.zIndex.modals + 2,
                }}
              >
                <F0Button
                  onClick={handleClose}
                  variant="outline"
                  size="md"
                  icon={Cross}
                  hideLabel
                  round
                  label="Close"
                />
              </Box>
              <Media content={content} />
              <Box padding="s16" paddingBottom="s24" gap="s24" alignItems="center">
                <Box gap="s8" alignItems="center" style={{ maxWidth: '90%', textAlign: 'center' }}>
                  <Text.Header2 weight="semibold" ellipsis lineClamp={2}>
                    {content.title}
                  </Text.Header2>
                  <Text.Normal size="300" ellipsis lineClamp={3}>
                    {content.description}
                  </Text.Normal>
                </Box>
                <Box flexDirection="row" gap="s16">
                  {content.buttons.map((button, index) => (
                    <CommsButton
                      key={index}
                      button={button}
                      size="md"
                      variant={
                        content.buttons.length === 1
                          ? 'default'
                          : index === 0
                            ? 'outline'
                            : 'default'
                      }
                      componentType="in_app_comms_modal"
                      componentName={componentName}
                      onUpsellSuccess={handleUpsellSuccess}
                      onUpsellError={handleUpsellError}
                      onClick={() => {
                        if (button.type !== 'CommsUpsellButtonRecord') {
                          onDismiss()
                        }
                      }}
                      upsellRequestComponentType="in_app_comms_modal"
                    />
                  ))}
                </Box>
              </Box>
            </Box>
            <UpsellRequestResponseDialog
              open={upsellSuccess}
              success={upsellSuccess}
              portalContainer={document.getElementById('root')}
              errorMessage={{
                title: i18n.t('upselling_kit.upselling_button.error_message.title'),
                description: i18n.t('upselling_kit.upselling_button.error_message.description'),
              }}
              successMessage={{
                title: i18n.t('upselling_kit.upselling_button.success_message.title_2'),
                description: i18n.t('upselling_kit.upselling_button.success_message.description'),
              }}
              closeLabel={i18n.t('upselling_kit.upselling_button.close_label')}
              onClose={handleUpsellClose}
            />
          </>
        )}
      </Box>
    </>
  )
}

export default AnnouncementModal
